import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Grid, Tooltip } from '@mui/material'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'

import {
  useGetIscrizioniQuery,
  useGetIscrizioneByIdQuery,
  useLazyGetBackendRQuery,
  useGetCurrentSeasonRangeQuery,
} from './api-slice'
import { colours } from '../settings/settings'

const fromDateToString = (date, utc = false) => {
  if (!(date instanceof Date)) return null

  let day, month, year

  if (utc) {
    day = String(date.getUTCDate())
    month = String(date.getUTCMonth() + 1)
    year = String(date.getUTCFullYear())
  } else {
    day = String(date.getDate())
    month = String(date.getMonth() + 1)
    year = String(date.getFullYear())
  }

  day = day.padStart(2, '0')
  month = month.padStart(2, '0')
  year = year.padStart(4, '0')

  return `${day}/${month}/${year}`
}

const fromDateTimeToString = (date, utc = false) => {
  if (!(date instanceof Date)) return null

  const dateString = fromDateToString(date, utc)

  let hours, minutes

  if (utc) {
    hours = String(date.getUTCHours())
    minutes = String(date.getUTCMinutes())
  } else {
    hours = String(date.getHours())
    minutes = String(date.getMinutes())
  }

  hours = hours.padStart(2, '0')
  minutes = minutes.padStart(2, '0')

  return `${dateString} ${hours}:${minutes}`
}

const fromStringToDate = date => {
  if (date?.split('/')?.length !== 3) return null

  const [day, month, year] = date.split('/')

  return new Date(year, month - 1, day)
}

const fromStringToTime = time => {
  if (time?.split(':')?.length !== 2) return null

  const [hours, minutes] = time.split(':').map(Number)

  return new Date(0, 0, 0, hours, minutes)
}

const fromStringsToDateTime = (date, time) => {
  if (date?.split('/')?.length !== 3) return null
  if (time?.split(':')?.length !== 2) return null

  const [day, month, year] = date.split('/')
  const [hours, minutes] = time.split(':')

  return new Date(year, month - 1, day, hours, minutes)
}

const fromStringToDecimalHours = time => {
  if (time?.split(':')?.length !== 2) return null

  const [hours, minutes] = time.split(':')

  return +hours + +minutes / 60
}

const fromYYYYMMDDtoDDMMYYYY = x =>
  x?.split('T')?.[0]?.split('-')?.reverse()?.join('/')

const mailTo = x => <a href={`mailto:${x}`}>{x}</a>

const getIndirizzo = (iscrizione, calendario) => {
  // get address from calendario
  if (calendario?.indirizzo && calendario?.citta) {
    return {
      presso: calendario.presso,
      indirizzo: calendario.indirizzo,
      citta: calendario.citta,
    }
  }

  // get address from iscrizione
  if (iscrizione?.indirizzo && iscrizione?.citta) {
    return {
      presso: iscrizione.presso,
      indirizzo: iscrizione.indirizzo,
      citta: iscrizione.citta,
    }
  }

  return {}
}

const formatIndirizzo = (info = {}, includePresso = true) => {
  const { presso, indirizzo, citta } = info

  if (!indirizzo || !citta) return undefined

  if (!presso || !includePresso) return `${indirizzo} - ${citta}`

  return `${presso} - ${indirizzo} - ${citta}`
}

const linkToGoogleMaps = fIndirizzo => {
  const text = 'Visualizza su Google Maps'
  const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    fIndirizzo,
  )}`

  return fIndirizzo ? (
    <a
      target='_blank'
      rel='noreferrer noopener'
      style={{ textDecoration: 'none', margin: 5 }}
      href={fIndirizzo ? url : ''}
    >
      {text}
    </a>
  ) : (
    <p style={{ margin: 5, color: 'grey' }}>{text}</p>
  )
}

const rError = /.*ERRORE.*/g
const rWarning = /.*WARNING.*/g
const rNewline = /\n/g
const rTimestamp = /.*\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}.*/g

const chColor = color => x => `<span style="color: ${color}">${x}</span>`

const formatLog = x => {
  x = x
    ?.replace(rError, chColor(colours.red))
    ?.replace(rWarning, chColor(colours.red))
    ?.replace(rTimestamp, chColor(colours.green))
    ?.replace(rNewline, '<br />')

  const lastLine = x
    ?.split('<br />')
    ?.filter(line => line.trim() !== '')
    ?.slice(-1)[0]

  const lastTimestamp = rTimestamp.test(lastLine)
  const lastFine = lastLine?.toLowerCase()?.includes('fine')

  if (x && (!lastTimestamp || !lastFine))
    x += `<br />${chColor(colours.red)("Log incompleto...<br />L'algoritmo potrebbe essere ancora in esecuzione o essere terminato in modo anomalo.")}`

  return x
}

const formatNameDeroga = (i, isSelectedTeam) => {
  const iName =
    (!i?.nomeSquadra && <i>Squadra non trovata</i>) ||
    (isSelectedTeam && <b>{i.nomeSquadra}</b>) ||
    i.nomeSquadra

  if (!i?.hasDeroga && !i?.isFuoriClassifica) return iName

  return (
    <Grid
      container
      alignItems='center'
      justifyContent='center'
      sx={{ width: '100%' }}
    >
      <Grid>{iName}</Grid>
      {i.hasDeroga && (
        <Grid>
          <Tooltip title='In deroga'>
            <PriorityHighIcon color='success' />
          </Tooltip>
        </Grid>
      )}
      {i.isFuoriClassifica && (
        <Grid>
          <Tooltip title='Fuori classifica'>
            <PriorityHighIcon color='error' />
          </Tooltip>
        </Grid>
      )}
    </Grid>
  )
}

const getArePubblicati = (
  selectedCampionatoGironiPubblicati,
  selectedCampionatoCalendariPubblicati,
  location,
) => {
  return ['gironi'].includes(location)
    ? selectedCampionatoGironiPubblicati
    : ['calendari', 'classifiche', 'finali'].includes(location)
      ? selectedCampionatoCalendariPubblicati
      : true
}

const DAYS_TO_MILLISECONDS = 24 * 60 * 60 * 1000

const isObjectId = id => /^[0-9a-fA-F]{24}$/.test(id)

const rangeFun = i => (i ? rangeFun(i - 1).concat(i) : [])

const sumFun = x => x?.reduce((a, b) => (a || 0) + (b || 0), 0)

const roundN = (n, d = 2) =>
  (Math.round(n * Math.pow(10, d)) / Math.pow(10, d)).toFixed(d)

const computeQuoziente = (a, b) =>
  a === 0 ? roundN(0) : b === 0 ? 'Max' : roundN(a / b)

const formatBool = x => (x === true && 'SÌ') || (x === false && 'NO') || '?'

const formatEuro = x => {
  if (typeof x !== 'number') return '-'

  let xx = x?.toFixed(2)

  if (x > 999) {
    const parts = xx.split('.')
    parts[0] = parts[0].replace(/\B(?=(\d{3})(?!\d))/g, ' ')
    xx = parts.join('.')
  }

  return `€ ${xx}`
}

const formatTabelloneName = x => `${x.squadre} squadre ${x.nome || ''}`

const formatBodyField = (value, toCase) =>
  (toCase === 'upper' && value?.toUpperCase()) ||
  (toCase === 'lower' && value?.toLowerCase()) ||
  (value === undefined ? null : value) // "undefined" is stripped by "JSON.stringify" so "null" is required to delete existing fields

const JSONreplacer = (key, value) =>
  ['_id', 'id', '__v'].includes(key) ? undefined : value

const groupByGirone = x =>
  (x?.girone === undefined && 'Senza girone') ||
  (x?.girone === 'finali' && 'Fasi finali') ||
  (x?.girone === 'avulsa' && 'Classifica avulsa') ||
  `Girone ${x?.girone}`

const setWinningScore = (n, isPropaganda, isPriFin2223) =>
  isPropaganda ? (isPriFin2223 ? 20 : 17) : n === 5 ? 15 : 25

const isDistintaReadOnly = (admin, gara) => {
  if (admin) return true
  const gDate = fromStringsToDateTime(gara?.data, gara?.ora)
  return gDate && gDate < new Date()
}

const filterByNumeroGara = (data, selectedNumeroGara) =>
  data?.filter(x =>
    selectedNumeroGara ? x.numeroGara === selectedNumeroGara : true,
  )

const filterByPeriod = (data, availablePeriodi, selectedPeriodo) =>
  data?.filter(x => {
    const xCheck = x.data && x.ora
    const xDate = fromStringsToDateTime(x.data, x.ora)
    const nDate = new Date()
    const nDatePlusN = N => new Date(Date.now() + N * DAYS_TO_MILLISECONDS)
    return selectedPeriodo === availablePeriodi[0] // Tutte
      ? true
      : selectedPeriodo === availablePeriodi[1] // Passate
        ? xCheck && xDate <= nDate
        : selectedPeriodo === availablePeriodi[2] // Future
          ? xCheck && xDate >= nDate
          : selectedPeriodo === availablePeriodi[3] // Prossimi 10 giorni
            ? xCheck && nDate <= xDate && xDate <= nDatePlusN(10)
            : selectedPeriodo === availablePeriodi[4] // Prossimi 30 giorni
              ? xCheck && nDate <= xDate && xDate <= nDatePlusN(30)
              : selectedPeriodo === availablePeriodi[5] // Senza data
                ? !xCheck
                : true
  })

const filterByRitardo = (data, availableRitardi, selectedRitardo) =>
  data?.filter(x => {
    const xDaysDelay = x.daysDelay
    return selectedRitardo === availableRitardi[0]
      ? true
      : selectedRitardo === availableRitardi[1]
        ? xDaysDelay < 10
        : selectedRitardo === availableRitardi[2]
          ? xDaysDelay >= 10 && xDaysDelay < 20
          : selectedRitardo === availableRitardi[3]
            ? xDaysDelay >= 20 && xDaysDelay < 30
            : selectedRitardo === availableRitardi[4]
              ? xDaysDelay >= 30 && xDaysDelay < 60
              : selectedRitardo === availableRitardi[5]
                ? xDaysDelay >= 60
                : false
  })

const filterByDesStatus = (data, STATUSES, selectedStatus, admin, arbitro) =>
  data?.filter(x =>
    selectedStatus === STATUSES[0] // Tutte
      ? admin || arbitro
      : selectedStatus === STATUSES[1] // In sospeso
        ? admin
          ? (x?.idDesignazione1 || x?.idDesignazione2) &&
            ((x?.idDesignazione1 &&
              x?.isValid1 &&
              ![true, false].includes(x?.acceptedByArbitro1)) ||
              (x?.idDesignazione2 &&
                x?.isValid2 &&
                ![true, false].includes(x?.acceptedByArbitro2)))
          : arbitro
            ? x?.isValid && ![true, false].includes(x?.acceptedByArbitro)
            : false
        : selectedStatus === STATUSES[2] // Accettate
          ? admin
            ? (x?.idDesignazione1 || x?.idDesignazione2) &&
              (!x?.idDesignazione1 || (x?.isValid1 && x?.acceptedByArbitro1)) &&
              (!x?.idDesignazione2 || (x?.isValid2 && x?.acceptedByArbitro2))
            : arbitro
              ? x?.isValid && x?.acceptedByArbitro
              : false
          : selectedStatus === STATUSES[3] // Rifiutate
            ? admin
              ? (x?.idDesignazione1 || x?.idDesignazione2) &&
                ((x?.idDesignazione1 &&
                  x?.isValid1 === false &&
                  x?.acceptedByArbitro1 === false) ||
                  (x?.idDesignazione2 &&
                    x?.isValid2 === false &&
                    x?.acceptedByArbitro2 === false))
              : arbitro
                ? x?.isValid === false && x?.acceptedByArbitro === false
                : false
            : selectedStatus === STATUSES[4] // Da designare || Annullate
              ? admin
                ? !x?.idDesignazione1 ||
                  x?.isValid1 === false ||
                  x?.isValid2 === false
                : arbitro
                  ? x?.isValid === false && x?.acceptedByArbitro !== false
                  : false
              : false,
  )

const filterByMissingInfo = (data, FILTERS, selectedFilter) =>
  data?.filter(x =>
    selectedFilter === FILTERS[0] // Tutte
      ? true
      : selectedFilter === FILTERS[1] // Senza data
        ? (!x.data || !x.ora) &&
          isObjectId(x.squadraCasaID) &&
          isObjectId(x.squadraTrasfertaID) &&
          typeof x.setVintiCasa !== 'number' &&
          typeof x.setVintiTrasferta !== 'number'
        : selectedFilter === FILTERS[2] // Da giocare
          ? x.data &&
            x.ora &&
            fromStringsToDateTime(x.data, x.ora) >= new Date() &&
            isObjectId(x.squadraCasaID) &&
            isObjectId(x.squadraTrasfertaID) &&
            (typeof x.setVintiCasa !== 'number' ||
              typeof x.setVintiTrasferta !== 'number')
          : true,
  )

const useTeamName = (id, stagione) => {
  const selectedCampionato = useSelector(
    state => state.selection.selectedCampionato,
  )

  const isPlaceholder = !isObjectId(id)

  const { data: iscrizioniNomi } = useGetIscrizioniQuery(
    { stagione, campionato: selectedCampionato, iFilter: 'nomi' },
    { skip: isPlaceholder || (!stagione && !selectedCampionato) },
  )

  const data = iscrizioniNomi?.find(i => i._id === id)

  return isPlaceholder ? id : data?.nomeSquadra || <i>Squadra non trovata</i>
}

const useIndirizzoGara = (gara, includePresso = true) => {
  const { data: squadraCasa } = useGetIscrizioneByIdQuery(gara?.squadraCasaID, {
    skip: !isObjectId(gara?.squadraCasaID),
  })

  return (
    formatIndirizzo(getIndirizzo(squadraCasa, gara), includePresso) || (
      <i>Indirizzo non trovato</i>
    )
  )
}

const useCheckBackendR = () => {
  const [triggerGetBackendR] = useLazyGetBackendRQuery()

  const checkBackendR = useCallback(async () => {
    const result = await triggerGetBackendR()

    if (result?.error)
      throw new Error('Funzionalità momentaneamente fuori uso.')

    if (!result?.data)
      throw new Error(
        'Funzionalità momentaneamente non disponibile. Riprovare tra qualche minuto.',
      )

    if (result?.data?.status !== 'available')
      throw new Error(
        'In questo momento la piattaforma è troppo impegnata. Riprovare tra qualche minuto.',
      )
  }, [triggerGetBackendR])

  return { checkBackendR }
}

const useSeasonRange = () => {
  const { data } = useGetCurrentSeasonRangeQuery()

  const minDate = data?.minDate ? new Date(data.minDate) : undefined
  const maxDate = data?.maxDate ? new Date(data.maxDate) : undefined

  return { minDate, maxDate }
}

export {
  fromDateToString,
  fromDateTimeToString,
  fromStringToDate,
  fromStringToTime,
  fromStringsToDateTime,
  fromStringToDecimalHours,
  fromYYYYMMDDtoDDMMYYYY,
  mailTo,
  getIndirizzo,
  formatIndirizzo,
  linkToGoogleMaps,
  formatLog,
  formatNameDeroga,
  getArePubblicati,
  DAYS_TO_MILLISECONDS,
  isObjectId,
  rangeFun,
  sumFun,
  roundN,
  computeQuoziente,
  formatBool,
  formatEuro,
  formatTabelloneName,
  formatBodyField,
  JSONreplacer,
  groupByGirone,
  setWinningScore,
  isDistintaReadOnly,
  filterByNumeroGara,
  filterByPeriod,
  filterByRitardo,
  filterByDesStatus,
  filterByMissingInfo,
  useTeamName,
  useIndirizzoGara,
  useCheckBackendR,
  useSeasonRange,
}
